import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {mediaQueries} from "@khan/shared-styles";

import Illustration from "./illustration.tsx";
import ListItem from "./list-item.tsx";
import {SerifHeadingMedium} from "./typography/typography.ts";

type ListItemProps = JSX.LibraryManagedAttributes<
    typeof ListItem,
    React.ComponentProps<typeof ListItem>
>;

type Props = {
    backgroundIllustration?: string;
    kind: "List";
    headline: string;
    listItems: Array<ListItemProps>;
    // TODO (russell): This would be most useful if it were common to all of
    // the marketing components.
    scrollId?: string;
};

export default class List extends React.Component<Props> {
    render(): React.ReactNode {
        const {headline, listItems, backgroundIllustration, scrollId} =
            this.props;
        return (
            <View style={styles.container}>
                {backgroundIllustration && (
                    <Illustration name={backgroundIllustration} />
                )}
                <View
                    style={[
                        styles.content,
                        backgroundIllustration
                            ? styles.contentWithBackground
                            : null,
                    ]}
                >
                    <SerifHeadingMedium
                        data-scroll-id={scrollId}
                        tag="h2"
                        style={styles.header}
                    >
                        {headline}
                    </SerifHeadingMedium>
                    <View style={styles.list}>
                        {listItems.map((listItem, i) => (
                            <React.Fragment key={i}>
                                <View style={styles.listItem}>
                                    <ListItem {...listItem} />
                                </View>
                                {i + 1 < listItems.length && (
                                    <View style={styles.shrinkableStrut} />
                                )}
                            </React.Fragment>
                        ))}
                    </View>
                </View>
            </View>
        );
    }
}

const styles = StyleSheet.create({
    container: {
        width: "100%",
        margin: "0 0 80px 0",
        ":focus": {
            outline: 0,
        },
    },
    content: {
        color: color.offBlack,
        width: "100%",
        maxWidth: 1152,
        padding: "0 64px",
        margin: "122px auto 0 auto",
        alignItems: "center",
        [mediaQueries.mdOrSmaller]: {
            paddingLeft: 20,
            paddingRight: 20,
        },
    },
    contentWithBackground: {
        minHeight: 631 - 112,
    },
    header: {
        // The -40px margin that is made up for with 40px padding is
        // so that when we scroll to this element, there is 40px of
        // space above the heading.
        margin: "-40px auto 0",
        padding: "40px 0 80px",
        textAlign: "center",
        maxWidth: 800,

        [mediaQueries.md]: {
            marginBottom: 71,
            fontSize: 44,
        },
        [mediaQueries.smOrSmaller]: {
            marginBottom: 8,
        },
        ":focus": {
            outline: 0,
        },
    },
    list: {
        flexDirection: "row",
        margin: "0 0 120px 0",
        [mediaQueries.mdOrSmaller]: {
            flexDirection: "column",
            alignItems: "center",
            marginBottom: 129,
        },
        [mediaQueries.smOrSmaller]: {
            marginBottom: 120,
            maxWidth: 327,
            marginLeft: "auto",
            marginRight: "auto",
        },
    },
    listItem: {
        display: "block",
        flex: 1,
    },
    shrinkableStrut: {
        flexBasis: 56,
        flexGrow: 0,
        flexShrink: 1,
        [mediaQueries.md]: {
            flexBasis: 60,
        },
        [mediaQueries.smOrSmaller]: {
            flexBasis: 48,
        },
    },
});
